import { useMemo } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertTriangle, Fuel, Gauge, TrendingUp } from 'lucide-react';
import { useConsumptionDeviation } from '@/api/hooks/useDepartments';
import type { CostCentreDeviationRow } from '@/types/departments';
import { KpiCard } from '@/components/fleetguardian/KpiCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const ZAR = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  maximumFractionDigits: 0,
});

const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });

// Above-standard marks carry a status hue, not a categorical one: this is a
// polarity encoding (over vs at/under the norm), so the two series are one
// diverging pair around the dashed rule, never two arbitrary palette colours.
const ABOVE_FILL = '#C0392B';

/**
 * Consumption vs the fleet standard.
 *
 * The spend chart on this page is size-dominated — it can only ever surface the
 * biggest departments. This section normalises PER VEHICLE, so a 7-vehicle unit
 * burning 3.5x the norm ranks above a head office spending ten times as much in
 * total. That inversion is the entire point: absolute spend is where theft
 * hides, because a small centre never reaches a top-20-by-spend list.
 *
 * Form is a scatter, not bars. The question is "is this point off the line?",
 * which is a relationship between two variables (fleet size and litres) — a bar
 * chart cannot express it. The dashed diagonal IS the standard (median
 * litres/vehicle x vehicles), so vertical distance above it is the consumption
 * that fleet size does not explain.
 */
export function DeviationSection(): JSX.Element {
  const { data, isLoading, error } = useConsumptionDeviation();

  const median = data?.median_litres_per_vehicle ?? 0;

  const maxVehicles = useMemo(
    () => Math.max(1, ...(data?.scatter ?? []).map((d) => d.vehicles)),
    [data],
  );

  // Two points are all a straight line needs, spanning the full x domain so it
  // reads as a rule across the plot rather than a short series.
  const standardLine = useMemo(
    () => [
      { vehicles: 0, litres: 0 },
      { vehicles: maxVehicles, litres: median * maxVehicles },
    ],
    [maxVehicles, median],
  );

  const above = useMemo(
    () => (data?.scatter ?? []).filter((d) => d.deviation_pct > 0),
    [data],
  );
  const below = useMemo(
    () => (data?.scatter ?? []).filter((d) => d.deviation_pct <= 0),
    [data],
  );

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Fleet standard"
          value={data ? `${NUM.format(Math.round(median))} L` : '—'}
          subtitle="median litres per vehicle · 90d"
          icon={Gauge}
          hue="blue"
        />
        <KpiCard
          label="Above standard"
          value={data ? NUM.format(data.cost_centres_above_standard) : '—'}
          subtitle={data ? `of ${NUM.format(data.cost_centres_scored)} scored (5+ vehicles)` : ''}
          icon={TrendingUp}
          hue="amber"
        />
        <KpiCard
          label="Unexplained litres"
          value={data ? `${NUM.format(Math.round(data.total_excess_litres))} L` : '—'}
          subtitle="beyond what fleet size explains · 90d"
          icon={Fuel}
          hue="orange"
        />
        <KpiCard
          label="Unexplained spend"
          value={data ? ZAR.format(data.total_excess_spend_rands) : '—'}
          subtitle="those litres at each centre's own R/L"
          icon={AlertTriangle}
          hue="red"
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            Consumption vs fleet size — distance above the line is unexplained fuel
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[26rem] px-2 py-2">
          {error ? (
            <div className="flex h-full items-center justify-center text-sm text-destructive">
              Could not load deviation data: {(error as Error).message}
            </div>
          ) : isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart margin={{ top: 8, right: 24, bottom: 28, left: 8 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  dataKey="vehicles"
                  name="Vehicles"
                  tick={{ fontSize: 10 }}
                  label={{
                    value: 'Vehicles in cost centre',
                    position: 'insideBottom',
                    offset: -16,
                    fontSize: 11,
                  }}
                />
                <YAxis
                  type="number"
                  dataKey="litres"
                  name="Litres"
                  tick={{ fontSize: 10 }}
                  width={64}
                  tickFormatter={(v: number) =>
                    v >= 1_000_000
                      ? `${(v / 1_000_000).toFixed(1)}M`
                      : `${Math.round(v / 1000)}k`
                  }
                />
                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload as Partial<CostCentreDeviationRow>;
                    // The standard-line series shares this tooltip but has no
                    // label; skip it rather than render an empty card.
                    if (!d?.label) return null;
                    return (
                      <div className="rounded-md border bg-popover p-2 text-xs shadow-md">
                        <div className="font-medium">{d.label}</div>
                        <div className="mt-1 tabular-nums text-muted-foreground">
                          {d.vehicles} vehicles ·{' '}
                          {NUM.format(Math.round(d.litres_per_vehicle ?? 0))} L/vehicle
                        </div>
                        <div className="tabular-nums">
                          {(d.deviation_pct ?? 0) >= 0 ? '+' : ''}
                          {(d.deviation_pct ?? 0).toFixed(0)}% vs standard
                        </div>
                        <div className="tabular-nums text-muted-foreground">
                          {ZAR.format(d.spend_rands ?? 0)} ·{' '}
                          {NUM.format(Math.round(d.excess_litres ?? 0))} L unexplained
                        </div>
                      </div>
                    );
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {/* Drawn before the scatters so the marks sit on top of the rule. */}
                <Line
                  data={standardLine}
                  dataKey="litres"
                  name="Fleet standard"
                  stroke="hsl(var(--muted-foreground))"
                  strokeDasharray="5 4"
                  strokeWidth={2}
                  dot={false}
                  legendType="plainline"
                  isAnimationActive={false}
                />
                <Scatter
                  name="At or below standard"
                  data={below}
                  fill="hsl(var(--muted-foreground))"
                  fillOpacity={0.45}
                />
                <Scatter
                  name="Above standard"
                  data={above}
                  fill={ABOVE_FILL}
                  fillOpacity={0.85}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            Biggest deviators — ranked by fuel their fleet size does not explain
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cost centre</TableHead>
                <TableHead>District</TableHead>
                <TableHead className="text-right">Vehicles</TableHead>
                <TableHead className="text-right">L / vehicle</TableHead>
                <TableHead className="text-right">vs standard</TableHead>
                <TableHead className="text-right">Unexplained L</TableHead>
                <TableHead className="text-right">Unexplained spend</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.top_deviators ?? []).map((d) => (
                <TableRow key={d.cost_centre_code}>
                  <TableCell className="font-mono text-xs">{d.cost_centre_code}</TableCell>
                  <TableCell className="text-sm">{d.district_desc ?? '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{d.vehicles}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {NUM.format(Math.round(d.litres_per_vehicle))}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums text-destructive">
                    +{d.deviation_pct.toFixed(0)}%
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {NUM.format(Math.round(d.excess_litres))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {ZAR.format(d.excess_spend_rands)}
                  </TableCell>
                </TableRow>
              ))}
              {!isLoading && !(data?.top_deviators ?? []).length && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="py-8 text-center text-sm text-muted-foreground"
                  >
                    No cost centre is above the fleet standard.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}

export default DeviationSection;
